/**
 * Firestore Service for pending_channels Collection
 *
 * Provides CRUD operations and queries for the pending_channels collection
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Query,
  DocumentData,
  Timestamp,
  serverTimestamp,
  WriteBatch,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import {
  PendingChannel,
  PendingChannelValidator,
  getDefaultPendingChannel,
  REJECTION_REASONS,
  RejectionReason,
} from '../types/pendingChannels';

const COLLECTION_NAME = 'pending_channels';

/**
 * Get reference to the pending_channels collection
 */
export const getPendingChannelsCollection = () => {
  return collection(db, COLLECTION_NAME);
};

/**
 * Get a single pending channel by document ID
 * @param docId The document ID
 * @returns The pending channel document or null if not found
 */
export const getPendingChannel = async (
  docId: string
): Promise<(PendingChannel & { _id: string }) | null> => {
  try {
    const docRef = doc(db, COLLECTION_NAME, docId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return {
      _id: docSnap.id,
      ...docSnap.data(),
    } as PendingChannel & { _id: string };
  } catch (error) {
    console.error('Error fetching pending channel:', error);
    throw error;
  }
};

/**
 * Get all pending channels (with optional filters)
 * @param filterQuery Optional where clause
 * @param orderByField Optional field to order by
 * @param limitCount Optional limit on results
 * @returns Array of pending channel documents
 */
export const getAllPendingChannels = async (
  filterQuery?: Array<[string, string, any]>,
  orderByField?: [string, 'asc' | 'desc'],
  limitCount?: number
): Promise<(PendingChannel & { _id: string })[]> => {
  try {
    let q: Query<DocumentData>;
    const constraints: any[] = [];

    if (filterQuery && filterQuery.length > 0) {
      filterQuery.forEach(([field, operator, value]) => {
        constraints.push(where(field, operator as any, value));
      });
    }

    if (orderByField) {
      constraints.push(orderBy(orderByField[0], orderByField[1]));
    }

    if (limitCount) {
      constraints.push(limit(limitCount));
    }

    if (constraints.length > 0) {
      q = query(getPendingChannelsCollection(), ...constraints);
    } else {
      q = query(getPendingChannelsCollection());
    }

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      _id: doc.id,
      ...doc.data(),
    })) as (PendingChannel & { _id: string })[];
  } catch (error) {
    console.error('Error fetching pending channels:', error);
    throw error;
  }
};

/**
 * Get pending channels by status
 * @param status The workflow status to filter by
 * @returns Array of pending channels with the specified status
 */
export const getPendingChannelsByStatus = async (
  status: string
): Promise<(PendingChannel & { _id: string })[]> => {
  return getAllPendingChannels([['workflow_status', '==', status]]);
};

/**
 * Get pending channels by discovery method
 * @param method The discovery method to filter by
 * @returns Array of pending channels discovered via the specified method
 */
export const getPendingChannelsByDiscoveryMethod = async (
  method: string
): Promise<(PendingChannel & { _id: string })[]> => {
  return getAllPendingChannels([['discovery_method', '==', method]]);
};

/**
 * Get pending channels ordered by subscriber count (descending)
 * @param limitCount Optional limit on results
 * @returns Array of pending channels sorted by subscriber count
 */
export const getPendingChannelsOrderedBySubscribers = async (
  limitCount?: number
): Promise<(PendingChannel & { _id: string })[]> => {
  return getAllPendingChannels(
    undefined,
    ['subscriber_count', 'desc'],
    limitCount
  );
};

/**
 * Get pending channels discovered after a certain date
 * @param date The start date
 * @returns Array of pending channels
 */
export const getPendingChannelsDiscoveredAfter = async (
  date: Date
): Promise<(PendingChannel & { _id: string })[]> => {
  return getAllPendingChannels([['discovery_date', '>=', Timestamp.fromDate(date)]]);
};

/**
 * Create a new pending channel document
 * @param channel The pending channel data (partial)
 * @param userId The user creating the channel
 * @returns The created document ID
 */
export const createPendingChannel = async (
  channel: Partial<PendingChannel>,
  userId: string
): Promise<string> => {
  try {
    // Merge with defaults and validate
    const channelData = getDefaultPendingChannel(channel);
    PendingChannelValidator.validateOrThrow(channelData);

    // Prepare document with timestamps
    const docData = {
      ...channelData,
      added_at: serverTimestamp(),
      added_by: userId,
      updated_at: serverTimestamp(),
      updated_by: userId,
      discovery_date: channelData.discovery_date || serverTimestamp(),
      outreach_date: null,
      approval_timestamp: null,
    };

    // Use channel ID as document ID if available, otherwise auto-generate
    const docId = channel.id || doc(getPendingChannelsCollection()).id;
    const docRef = doc(db, COLLECTION_NAME, docId);

    await setDoc(docRef, docData);
    return docId;
  } catch (error) {
    console.error('Error creating pending channel:', error);
    throw error;
  }
};

/**
 * Update an existing pending channel document
 * @param docId The document ID
 * @param updates The fields to update
 * @param userId The user performing the update
 */
export const updatePendingChannel = async (
  docId: string,
  updates: Partial<PendingChannel>,
  userId: string
): Promise<void> => {
  try {
    const docRef = doc(db, COLLECTION_NAME, docId);

    const updateData = {
      ...updates,
      updated_at: serverTimestamp(),
      updated_by: userId,
    };

    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error('Error updating pending channel:', error);
    throw error;
  }
};

/**
 * Delete a pending channel document
 * @param docId The document ID
 */
export const deletePendingChannel = async (docId: string): Promise<void> => {
  try {
    const docRef = doc(db, COLLECTION_NAME, docId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting pending channel:', error);
    throw error;
  }
};

/**
 * Batch create multiple pending channels
 * @param channels Array of pending channel data
 * @param userId The user creating the channels
 * @returns Array of created document IDs
 */
export const batchCreatePendingChannels = async (
  channels: Partial<PendingChannel>[],
  userId: string
): Promise<string[]> => {
  try {
    const batch: WriteBatch = writeBatch(db);
    const docIds: string[] = [];

    channels.forEach((channel) => {
      // Validate
      const channelData = getDefaultPendingChannel(channel);
      PendingChannelValidator.validateOrThrow(channelData);

      // Prepare document
      const docData = {
        ...channelData,
        added_at: serverTimestamp(),
        added_by: userId,
        updated_at: serverTimestamp(),
        updated_by: userId,
        discovery_date: channelData.discovery_date || serverTimestamp(),
      };

      // Use channel ID as document ID if available
      const docId = channel.id || doc(getPendingChannelsCollection()).id;
      const docRef = doc(db, COLLECTION_NAME, docId);

      batch.set(docRef, docData);
      docIds.push(docId);
    });

    await batch.commit();
    return docIds;
  } catch (error) {
    console.error('Error batch creating pending channels:', error);
    throw error;
  }
};

/**
 * Batch update multiple pending channels
 * @param updates Map of document ID to partial updates
 * @param userId The user performing the updates
 */
export const batchUpdatePendingChannels = async (
  updates: Record<string, Partial<PendingChannel>>,
  userId: string
): Promise<void> => {
  try {
    const batch: WriteBatch = writeBatch(db);

    Object.entries(updates).forEach(([docId, updateData]) => {
      const docRef = doc(db, COLLECTION_NAME, docId);
      batch.update(docRef, {
        ...updateData,
        updated_at: serverTimestamp(),
        updated_by: userId,
      });
    });

    await batch.commit();
  } catch (error) {
    console.error('Error batch updating pending channels:', error);
    throw error;
  }
};

/**
 * Move a pending channel to another collection (approval workflow)
 * @param docId The document ID
 * @param targetCollection The target collection name
 * @param approver The user approving the move
 */
export const approvePendingChannel = async (
  docId: string,
  targetCollection: string,
  approver: string
): Promise<void> => {
  try {
    // Get the document
    const channel = await getPendingChannel(docId);
    if (!channel) {
      throw new Error('Channel not found');
    }

    // Update approval metadata
    await updatePendingChannel(
      docId,
      {
        approval_approver: approver,
        approval_timestamp: serverTimestamp() as any,
        approval_target_table: targetCollection,
        workflow_status: 'approved',
      },
      approver
    );
  } catch (error) {
    console.error('Error approving pending channel:', error);
    throw error;
  }
};

/**
 * Reject a pending channel with reason and optional notes
 * @param docId The document ID
 * @param reason The rejection reason (must be a valid RejectionReason)
 * @param rejectedBy The user rejecting the channel
 * @param notes Optional rejection notes (required if reason is 'other')
 */
export const rejectPendingChannel = async (
  docId: string,
  reason: RejectionReason,
  rejectedBy: string,
  notes?: string
): Promise<PendingChannel & { _id: string }> => {
  try {
    // Validate rejection reason
    const validReasons = Object.values(REJECTION_REASONS);
    if (!validReasons.includes(reason)) {
      throw new Error(`Invalid rejection reason: ${reason}. Must be one of: ${validReasons.join(', ')}`);
    }

    // If reason is 'other', notes must be provided
    if (reason === REJECTION_REASONS.OTHER && (!notes || notes.trim() === '')) {
      throw new Error('Notes are required when rejection reason is "other"');
    }

    // Get the document
    const channel = await getPendingChannel(docId);
    if (!channel) {
      throw new Error('Channel not found');
    }

    // Check if channel is in a rejectable state
    if (channel.workflow_status === 'rejected') {
      throw new Error('Channel is already rejected');
    }
    if (channel.workflow_status === 'approved') {
      throw new Error('Cannot reject an already approved channel');
    }

    // Update rejection metadata
    const docRef = doc(db, COLLECTION_NAME, docId);
    const updateData = {
      workflow_status: 'rejected',
      rejection_reason: reason,
      rejection_notes: notes || '',
      rejected_at: serverTimestamp(),
      rejected_by: rejectedBy,
      updated_at: serverTimestamp(),
      updated_by: rejectedBy,
    };

    await updateDoc(docRef, updateData);

    // Return the updated channel
    const updatedChannel = await getPendingChannel(docId);
    if (!updatedChannel) {
      throw new Error('Failed to retrieve updated channel');
    }

    return updatedChannel;
  } catch (error) {
    console.error('Error rejecting pending channel:', error);
    throw error;
  }
};

/**
 * Target table type for bulk approval
 */
export type ApprovalTargetTable = 'youtube_reaction' | 'youtube_playlisting';

/**
 * Bulk approval validation error for a single channel
 */
export interface BulkApprovalChannelError {
  channel_id: string;
  error: string;
}

/**
 * Bulk approval result
 */
export interface BulkApprovalResult {
  approved: number;
  failed: number;
  errors: BulkApprovalChannelError[];
}

/**
 * Bulk approve multiple pending channels with all-or-nothing validation
 * 
 * Validation Strategy (all-or-nothing):
 * - Fetch ALL channels in parallel
 * - Validate each: exists, has email, correct status, not duplicate
 * - If ANY fails: return error with details (no channels approved)
 * 
 * After validation passes:
 * - Split into batches of 125 channels (250 operations for safety - Firestore limit 500)
 * - Use batch writes to add to target table and update pending_channels
 * 
 * @param channelIds Array of channel document IDs to approve
 * @param targetTable The target collection (youtube_reaction or youtube_playlisting)
 * @param approver The user performing the approval
 * @param notes Optional notes to apply to all channels
 * @returns Result with approved/failed counts and error details
 */
export const bulkApprovePendingChannels = async (
  channelIds: string[],
  targetTable: ApprovalTargetTable,
  approver: string,
  notes?: string
): Promise<BulkApprovalResult> => {
  const errors: BulkApprovalChannelError[] = [];

  // Phase 1: Fetch all channels in parallel
  const channelPromises = channelIds.map(async (channelId) => {
    try {
      const channel = await getPendingChannel(channelId);
      return { channelId, channel, error: null };
    } catch (err) {
      return { channelId, channel: null, error: 'Failed to fetch channel' };
    }
  });

  const channelResults = await Promise.all(channelPromises);

  // Phase 2: Validate each channel
  const validChannels: { channelId: string; channel: PendingChannel & { _id: string } }[] = [];

  for (const result of channelResults) {
    const { channelId, channel, error: fetchError } = result;

    // Check fetch error
    if (fetchError) {
      errors.push({ channel_id: channelId, error: fetchError });
      continue;
    }

    // Check if channel exists
    if (!channel) {
      errors.push({ channel_id: channelId, error: 'Channel not found' });
      continue;
    }

    // Check if email is populated
    if (!channel.email || channel.email.trim() === '') {
      errors.push({ channel_id: channelId, error: 'Missing email' });
      continue;
    }

    // Check if workflow_status is 'pending' or 'review'
    if (!['pending', 'review'].includes(channel.workflow_status)) {
      errors.push({
        channel_id: channelId,
        error: `Invalid workflow status: ${channel.workflow_status}. Must be 'pending' or 'review'`,
      });
      continue;
    }

    validChannels.push({ channelId, channel });
  }

  // Phase 3: Check for duplicates in target table (in parallel)
  const targetCollection = collection(db, targetTable);
  const duplicateCheckPromises = validChannels.map(async ({ channelId, channel }) => {
    try {
      // Check if channel already exists in target table by channel ID
      const targetDocRef = doc(db, targetTable, channel.id);
      const targetDoc = await getDoc(targetDocRef);

      if (targetDoc.exists()) {
        return { channelId, isDuplicate: true };
      }
      return { channelId, isDuplicate: false };
    } catch (err) {
      // If we can't check, assume not duplicate and let batch write handle it
      console.warn(`Failed to check duplicate for ${channelId}:`, err);
      return { channelId, isDuplicate: false };
    }
  });

  const duplicateResults = await Promise.all(duplicateCheckPromises);

  // Remove duplicates from valid channels and add to errors
  const nonDuplicateChannels = validChannels.filter(({ channelId }) => {
    const dupResult = duplicateResults.find((r) => r.channelId === channelId);
    if (dupResult?.isDuplicate) {
      errors.push({ channel_id: channelId, error: `Channel already exists in ${targetTable}` });
      return false;
    }
    return true;
  });

  // Phase 4: If ANY validation errors, return without approving
  if (errors.length > 0) {
    return {
      approved: 0,
      failed: errors.length,
      errors,
    };
  }

  // Phase 5: Batch writes (125 channels per batch = 250 operations)
  const BATCH_SIZE = 125;
  const batches: { channelId: string; channel: PendingChannel & { _id: string } }[][] = [];

  for (let i = 0; i < nonDuplicateChannels.length; i += BATCH_SIZE) {
    batches.push(nonDuplicateChannels.slice(i, i + BATCH_SIZE));
  }

  let approvedCount = 0;
  const batchErrors: BulkApprovalChannelError[] = [];

  for (const batchItems of batches) {
    try {
      const batch = writeBatch(db);

      for (const { channelId, channel } of batchItems) {
        // Operation 1: Add channel to target table
        const targetDocRef = doc(db, targetTable, channel.id);
        const approvalData = {
          ...channel,
          approval_approver: approver,
          approval_timestamp: serverTimestamp(),
          approval_target_table: targetTable,
          notes: notes || channel.notes || '',
          added_at: serverTimestamp(),
          updated_at: serverTimestamp(),
          updated_by: approver,
        };
        batch.set(targetDocRef, approvalData);

        // Operation 2: Update pending_channels status
        const pendingDocRef = doc(db, COLLECTION_NAME, channelId);
        batch.update(pendingDocRef, {
          workflow_status: 'approved',
          approval_approver: approver,
          approval_timestamp: serverTimestamp(),
          approval_target_table: targetTable,
          notes: notes || channel.notes || '',
          updated_at: serverTimestamp(),
          updated_by: approver,
        });
      }

      await batch.commit();
      approvedCount += batchItems.length;
    } catch (error) {
      console.error('Batch write failed:', error);
      // Mark all channels in this batch as failed
      for (const { channelId } of batchItems) {
        batchErrors.push({ channel_id: channelId, error: 'Batch write failed' });
      }
    }
  }

  return {
    approved: approvedCount,
    failed: batchErrors.length,
    errors: batchErrors,
  };
};

export type { PendingChannel, RejectionReason };
export { PendingChannelValidator, REJECTION_REASONS };
